"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _classnames = _interopRequireDefault(require("classnames"));
var _NavigationLink = _interopRequireDefault(require("./NavigationLink"));
var _NavigationMenu = _interopRequireDefault(require("./NavigationMenu"));
var _SearchBox = _interopRequireDefault(require("../SearchBox"));
var _hooks = require("../../hooks");
var _enums = require("../../enums");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/**
 * Narrow the type of the nav item to a NavMenu.
 */
const isMenu = item => "items" in item;

/**
 * Narrow the type of the logo prop to LogoProps.
 */
const isLogoProps = logo => ! /*#__PURE__*/(0, _react.isValidElement)(logo);

/**
 * Display the standard logo if the props were provided otherwise display the
 * full element provided.
 */
const generateLogo = (logo, generateLink) => {
  if (isLogoProps(logo)) {
    const {
      url,
      src,
      title,
      icon,
      "aria-current": ariaCurrent,
      "aria-label": ariaLabel,
      ...logoProps
    } = logo;
    const content = /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("div", {
      className: "p-navigation__logo-tag"
    }, icon !== null && icon !== void 0 ? icon : /*#__PURE__*/_react.default.createElement("img", {
      className: "p-navigation__logo-icon",
      src: src,
      alt: "Logo"
    })), /*#__PURE__*/_react.default.createElement("span", {
      className: "p-navigation__logo-title"
    }, title));
    return /*#__PURE__*/_react.default.createElement("div", _extends({
      className: "p-navigation__tagged-logo"
    }, logoProps), /*#__PURE__*/_react.default.createElement(_NavigationLink.default, {
      generateLink: generateLink,
      link: {
        "aria-label": ariaLabel,
        className: "p-navigation__link",
        isSelected: !!ariaCurrent,
        label: content,
        url: url
      }
    }));
  }
  return /*#__PURE__*/_react.default.createElement("div", {
    className: "p-navigation__logo"
  }, logo);
};

/**
 * Generate the JSX for a set of nav items. This will map the items to menus,
 * links or generated components.
 * @param items The nav items to map to elements.
 * @param closeMobileMenu A function to close the mobile menu.
 * @param generateLink The optional function used to generate link components.
 * @returns A list of navigation item elements.
 */
const generateItems = (items, closeMobileMenu, generateLink) => items.map((item, i) => isMenu(item) ? /*#__PURE__*/_react.default.createElement(_NavigationMenu.default, _extends({}, item, {
  generateLink: generateLink,
  key: i
})) : /*#__PURE__*/_react.default.createElement("li", {
  className: (0, _classnames.default)("p-navigation__item", {
    "is-selected": item.isSelected
  }),
  key: i
}, /*#__PURE__*/_react.default.createElement(_NavigationLink.default, {
  generateLink: generateLink,
  link: {
    ...item,
    className: (0, _classnames.default)("p-navigation__link", item.className),
    onClick: evt => {
      var _item$onClick;
      (_item$onClick = item.onClick) === null || _item$onClick === void 0 || _item$onClick.call(item, evt);
      closeMobileMenu();
    }
  }
})));

/**
This is the [React](https://reactjs.org/) component for the Vanilla
[Navigation](https://vanillaframework.io/docs/patterns/navigation) pattern. It
is a simple navigation bar that you can add to the top of your site or app.

The navigation items are collapsed behind a "Menu" link in small screens and
displayed horizontally on larger screens.
 */
const Navigation = _ref => {
  let {
    fullWidth,
    generateLink,
    items,
    itemsRight,
    leftNavProps,
    logo,
    navProps,
    rightNavProps,
    searchProps,
    theme,
    ...headerProps
  } = _ref;
  const searchRef = (0, _react.useRef)(null);
  const [mobileMenuOpen, setMobileMenuOpen] = (0, _react.useState)(false);
  const [searchOpen, setSearchOpen] = (0, _react.useState)(false);
  // Display the search box if the props have been provided.
  const hasSearch = !!searchProps;
  // Close the mobile menu when the search box is opened.
  const toggleSearch = open => {
    setSearchOpen(open !== null && open !== void 0 ? open : !searchOpen);
    setMobileMenuOpen(false);
  };
  // Close the search box when the mobile menu is opened.
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setSearchOpen(false);
  };
  const closeMobileMenu = () => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };
  // Hide the searchbox when the escape key is pressed.
  (0, _hooks.useOnEscapePressed)(() => toggleSearch(false));
  (0, _react.useEffect)(() => {
    if (searchOpen) {
      var _searchRef$current;
      // Focus on the searchbox when it appears. This done in a useEffect so
      // that the state change to display the searchbox has already occured and
      // the input has been made visible.
      (_searchRef$current = searchRef.current) === null || _searchRef$current === void 0 || _searchRef$current.focus();
    }
  }, [searchOpen]);
  return /*#__PURE__*/_react.default.createElement("header", _extends({}, headerProps, {
    className: (0, _classnames.default)("p-navigation", headerProps.className, {
      "has-menu-open": mobileMenuOpen,
      "has-search-open": searchOpen,
      "is-dark": theme === _enums.Theme.DARK,
      "is-light": theme === _enums.Theme.LIGHT
    })
  }), /*#__PURE__*/_react.default.createElement("div", {
    className: fullWidth ? "p-navigation__row--full-width" : "p-navigation__row"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "p-navigation__banner"
  }, generateLogo(logo, generateLink), /*#__PURE__*/_react.default.createElement("ul", {
    className: "p-navigation__items"
  },
  // When the header has a search box then this button is used to
  // toggle the search box at mobile size.
  hasSearch ? /*#__PURE__*/_react.default.createElement("li", {
    className: "p-navigation__item"
  }, /*#__PURE__*/_react.default.createElement("button", {
    "aria-label": "Search",
    className: "p-navigation__link--search-toggle",
    onClick: () => toggleSearch()
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "p-navigation__search-label"
  }, "Search"))) : null, (items === null || items === void 0 ? void 0 : items.length) > 0 || (itemsRight === null || itemsRight === void 0 ? void 0 : itemsRight.length) > 0 ? /*#__PURE__*/_react.default.createElement("li", {
    className: "p-navigation__item"
  }, /*#__PURE__*/_react.default.createElement("button", {
    "aria-pressed": mobileMenuOpen,
    className: "p-navigation__link",
    onClick: toggleMobileMenu
  }, mobileMenuOpen ? "Close menu" : "Menu")) : null)), /*#__PURE__*/_react.default.createElement("nav", _extends({
    className: "p-navigation__nav"
  }, navProps), /*#__PURE__*/_react.default.createElement("ul", _extends({
    className: "p-navigation__items"
  }, leftNavProps), items ? generateItems(items, closeMobileMenu, generateLink) : null), itemsRight || hasSearch ? /*#__PURE__*/_react.default.createElement("ul", _extends({
    className: "p-navigation__items"
  }, rightNavProps), itemsRight ? generateItems(itemsRight, closeMobileMenu, generateLink) : null,
  // When the header has a search box then this button is used to
  // toggle the search box at non-mobile size.
  hasSearch ? /*#__PURE__*/_react.default.createElement("li", {
    className: "p-navigation__item"
  }, /*#__PURE__*/_react.default.createElement("button", {
    "aria-label": "Search",
    className: "p-navigation__link--search-toggle",
    onClick: () => toggleSearch()
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "p-navigation__search-label"
  }, "Search"))) : null) : null,
  // When the header has a search box and the user has opened the search
  // form then this search box is displayed.
  hasSearch ? /*#__PURE__*/_react.default.createElement("div", {
    className: "p-navigation__search",
    "aria-hidden": !searchOpen
  }, /*#__PURE__*/_react.default.createElement(_SearchBox.default, {
    ref: searchRef
  })) : null)),
  // When the header has a search box and the user has opened the search
  // form then this element is overlayed over the whole page.
  hasSearch ? /*#__PURE__*/_react.default.createElement("div", {
    className: "p-navigation__search-overlay",
    onClick: () => setSearchOpen(false)
  }) : null);
};
var _default = exports.default = Navigation;